import CheckIcon from '@mui/icons-material/Check';
import { Box, ListItemButton, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import type { SortOption, sortByStoreProps } from './types';

export const ListOptions = <TSortValue extends string, TFilterOptions extends string>({
  isOpen,
  activeIndex,
  handleSelectSortBy,
  sortOptions,
  useStore,
  sortByLabel,
}: {
  isOpen: boolean;
  activeIndex: number;
  handleSelectSortBy: (sortValue: TSortValue) => void;
  sortOptions: readonly SortOption<TSortValue>[];
  useStore: sortByStoreProps<TSortValue, TFilterOptions>;
  sortByLabel: string;
}) => {
  const { sortBy } = useStore(
    ({ sortBy }) => ({
      sortBy,
    }),
    shallow,
  );

  const listOptionsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const setItemListItemRef =
    (listItemIndex: number): React.RefCallback<HTMLDivElement> =>
    listItemRef => {
      listOptionsRefs.current[listItemIndex] = listItemRef;
    };

  useEffect(() => {
    if (isOpen) {
      listOptionsRefs.current[activeIndex]?.focus({ preventScroll: true });
    }
  }, [isOpen, activeIndex]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1,
          height: 36,
          width: '100%',
          bgcolor: 'white',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 14 }}>
          {sortByLabel}
        </Typography>
      </Box>
      {Object.values(sortOptions).map(({ label, value }, idx) => {
        const selected = sortBy === value;
        return (
          <ListItemButton
            key={value}
            id={`option-${value}`}
            ref={setItemListItemRef(idx)}
            role="option"
            aria-selected={selected}
            tabIndex={idx === activeIndex ? 0 : -1}
            onClick={() => handleSelectSortBy(value)}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1,
              height: 36,
              width: '100%',
              bgcolor: selected ? 'white' : 'transparent',
              '&:hover': {
                bgcolor: 'neutral.lightest',
              },
              '&:focus-visible, &.Mui-focusVisible': {
                outline: '2px solid #4B99E6',
                outlineOffset: '-2px',
                backgroundColor: 'transparent',
              },
            }}
          >
            <Typography sx={{ color: 'text.secondary', fontWeight: 400, fontSize: 14 }}>
              {label}
            </Typography>
            <Box sx={{ width: 24, display: 'flex', justifyContent: 'flex-end' }}>
              {selected && <CheckIcon fontSize="small" sx={{ color: 'greens.main' }} />}
            </Box>
          </ListItemButton>
        );
      })}
    </>
  );
};
